import React, { useEffect, useState } from "react";
import { Check, ArrowUpRight, Sparkles, MessageCircle } from "lucide-react";
import MarketingShell from "../components/marketing/MarketingShell";
import DemoModal from "../components/marketing/DemoModal";

// Public pricing is hidden by request — sellers connect for a custom quotation.
// WhatsApp / email handles all sales conversations.
const QUOTATION_WHATSAPP =
  "https://wa.me/919464645491?text=" +
  encodeURIComponent(
    "Hi BrandBorn — I'd like a quotation for the AI listings plan. Please share details."
  );
const QUOTATION_EMAIL =
  "mailto:info@ecomcatalog.ai?subject=" +
  encodeURIComponent("Quotation Request — BrandBorn AI Plans") +
  "&body=" +
  encodeURIComponent(
    "Hi BrandBorn team,\n\nI'm interested in your AI listing plans. Please share a custom quotation for my brand.\n\nBrand name:\nApprox SKUs / month:\nMarketplaces:\n\nThanks."
  );

const PLANS = [
  {
    id: "starter_100",
    tier: "Starter",
    tag: "Get started",
    listings: 100,
    desc: "For new brands shipping their first batch of SKUs.",
    features: [
      "100 AI listings",
      "Strict Myntra invariants formatting",
      "Marketplace-ready Excel export",
      "Image hash caching (70% LLM savings)",
      "Email support",
    ],
    highlight: false,
  },
  {
    id: "pro_500",
    tier: "Pro",
    tag: "Most popular",
    listings: 500,
    desc: "Sweet spot for growing D2C brands. Best price-per-listing.",
    features: [
      "500 AI listings",
      "Data Analysis dashboard",
      "Brand Growth tab",
      "Ads Suggestions (AI scale / test / avoid)",
      "Monthly Sales Report data",
      "Competitor Analysis (Myntra-only)",
      "Priority WhatsApp support",
    ],
    highlight: true,
  },
  {
    id: "business_1000",
    tier: "Business",
    tag: "Full access",
    listings: 1000,
    desc: "Everything in Pro plus the full workspace for scaling brands.",
    features: [
      "1000 AI listings · lowest cost / listing",
      "Everything in Pro",
      "My Catalogue workspace",
      "Dedicated success manager",
    ],
    highlight: false,
  },
];

const COMPARE_GROUPS = [
  {
    label: "Catalog generation",
    rows: [
      { f: "AI listings included",     v: ["100",       "500",          "1000"] },
      { f: "Strict Myntra invariants",  v: ["Yes",      "Yes",          "Yes"] },
      { f: "Bulk SKU upload",           v: ["—",         "Yes",          "Yes"] },
    ],
  },
  {
    label: "Brand growth & insights",
    rows: [
      { f: "Data Analysis dashboard",   v: ["—",         "Yes",          "Yes"] },
      { f: "Brand Growth tab",          v: ["—",         "Yes",          "Yes"] },
      { f: "Ads Suggestions (AI)",      v: ["—",         "Yes",          "Yes"] },
      { f: "Monthly Sales Report",      v: ["—",         "Yes",          "Yes"] },
      { f: "Competitor Analysis",       v: ["—",         "Yes (Myntra)", "Yes (Myntra)"] },
    ],
  },
  {
    label: "Workspace",
    rows: [
      { f: "My Catalogue workspace",    v: ["—",         "—",            "Yes"] },
    ],
  },
  {
    label: "Support",
    rows: [
      { f: "Channel",                   v: ["Email",    "Priority WhatsApp", "Dedicated manager"] },
      { f: "Credits expire?",           v: ["Never",    "Never",        "Never"] },
    ],
  },
];

export default function Pricing() {
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "Pricing — BrandBorn AI · 100, 500 & 1000 listing packs";
  }, []);

  return (
    <MarketingShell>
      <section className="pt-16 sm:pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
            Pricing
          </div>
          <h1 className="font-mkt-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter mt-4 leading-[1.02] max-w-4xl mx-auto">
            Pick the plan that fits your <span className="mkt-gradient-text-vivid">brand.</span>
          </h1>
          <p className="text-lg text-slate-400 mt-6 max-w-2xl mx-auto leading-relaxed">
            Custom quotation tailored to your catalog size and marketplaces. Connect with us on
            WhatsApp or email — we&apos;ll share a personalised plan within minutes.
          </p>
        </div>
      </section>

      {/* Plan cards */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`relative p-7 rounded-3xl border ${
                  p.highlight
                    ? "border-indigo-400/40 bg-gradient-to-br from-indigo-500/12 via-purple-500/6 to-transparent shadow-[0_30px_80px_-20px_rgba(124,58,237,0.55)]"
                    : "border-white/[0.08] bg-white/[0.02]"
                }`}
                data-testid={`plan-${p.id}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-[10px] uppercase tracking-[0.2em] font-bold text-white">
                    {p.tag}
                  </div>
                )}
                {!p.highlight && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                    {p.tag}
                  </div>
                )}
                <div className="font-mkt-display text-3xl font-semibold mt-2">{p.tier}</div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
                  {p.listings} AI listings · custom quote
                </div>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed min-h-[48px]">{p.desc}</p>
                <a
                  href={QUOTATION_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-5 inline-flex w-full justify-center items-center gap-2 ${
                    p.highlight ? "mkt-btn-gradient" : "mkt-btn-ghost"
                  }`}
                  data-testid={`plan-cta-${p.id}`}
                >
                  <MessageCircle size={14} /> Connect for Quotation <ArrowUpRight size={14} />
                </a>
                <ul className="mt-7 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check size={15} className="text-indigo-400 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center text-xs text-slate-500">
            All plans · Credits never expire · GST exclusive · WhatsApp +91 94646 45491 · info@ecomcatalog.ai
          </div>

          {/* Onboard Your Brand — secondary CTA below the plan cards */}
          <div className="mt-12 flex flex-col items-center gap-3" data-testid="onboard-brand-cta-wrap">
            <a
              href="https://www.brandborn.in"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white overflow-hidden transition-all hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #ec4899 55%, #7c3aed 100%)",
                boxShadow: "0 18px 42px -12px rgba(236,72,153,0.55), inset 0 1px 0 rgba(255,255,255,0.15)",
              }}
              data-testid="onboard-brand-btn"
            >
              <Sparkles size={15} className="text-yellow-100" />
              Onboard Your Brand
              <ArrowUpRight size={15} />
            </a>
            <p className="text-xs text-slate-500">
              Get onboarded across Myntra, Nykaa, Ajio, Amazon, Flipkart, Tata CLiQ, Blinkit & more →{" "}
              <span className="text-indigo-300 font-mono">brandborn.in</span>
            </p>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Compare plans
            </div>
            <h2 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">
              The fine print, in one view.
            </h2>
          </div>

          <div className="mkt-glass overflow-hidden">
            <div className="grid grid-cols-4 px-5 sm:px-7 py-4 bg-white/[0.03] border-b border-white/[0.06] text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-400">
              <div>Feature</div>
              <div className="text-center">Starter</div>
              <div className="text-center text-indigo-300">Pro</div>
              <div className="text-center">Business</div>
            </div>
            {COMPARE_GROUPS.map((g) => (
              <div key={g.label}>
                <div className="px-5 sm:px-7 py-3 bg-white/[0.015] border-b border-white/[0.04] text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold">
                  {g.label}
                </div>
                {g.rows.map((r) => (
                  <div
                    key={r.f}
                    className="grid grid-cols-4 px-5 sm:px-7 py-4 border-b border-white/[0.04] last:border-b-0 text-sm text-slate-200"
                  >
                    <div className="text-slate-300">{r.f}</div>
                    {r.v.map((cell, i) => (
                      <div key={i} className={`text-center ${i === 1 ? "text-indigo-200 font-medium" : "text-slate-300"}`}>
                        {cell === "Yes" ? <Check size={15} className="inline text-indigo-400" /> : cell === "—" ? <span className="text-slate-600">—</span> : cell}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="mkt-glass-strong p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mkt-glass text-[10px] uppercase tracking-[0.25em] text-indigo-300 font-bold mb-4">
                <Sparkles size={11} /> Ready when you are
              </div>
              <h3 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                Pick the plan that fits your <span className="mkt-gradient-text-vivid">brand.</span>
              </h3>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <a
                  href={QUOTATION_WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mkt-btn-gradient inline-flex items-center gap-2"
                  data-testid="cta-bottom-whatsapp"
                >
                  <MessageCircle size={14} /> Connect on WhatsApp <ArrowUpRight size={14} />
                </a>
                <a
                  href={QUOTATION_EMAIL}
                  className="mkt-btn-ghost"
                  data-testid="cta-bottom-email"
                >
                  Email for Quotation
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </MarketingShell>
  );
}
